import { useState } from "react";
import { Star, RefreshCw, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { toast } from "sonner";

let defaultsPromise = null;
const loadDefaults = () => {
  if (!defaultsPromise) defaultsPromise = api.get("/staff/feature-defaults").then((r) => r.data).catch(() => { defaultsPromise = null; return { days: 7, auto_renew: false, options: [0, 3, 7, 14, 30] }; });
  return defaultsPromise;
};
export const resetFeatureDefaultsCache = () => { defaultsPromise = null; };

export const featureTimeLeft = (until) => {
  if (!until) return "";
  const ms = new Date(until) - Date.now();
  if (ms <= 0) return "ending";
  return ms >= 86400000 ? `${Math.round(ms / 86400000)}d left` : `${Math.max(1, Math.ceil(ms / 3600000))}h left`;
};

const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

// Staff-only star on listing cards: unfeature instantly, or pick a duration to feature.
export function FeatureMenu({ listing, feat, scheduled, onChange, bottom }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [days, setDays] = useState(7);
  const [renew, setRenew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const post = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/listings/${listing.id}/feature`, body);
      onChange(data);
      setOpen(false);
      toast.success(body.featured ? (body.start && body.start > new Date().toISOString().slice(0, 10) ? `Scheduled from ${body.start}${body.end ? ` to ${body.end}` : ""}` : body.end ? `Featured until ${body.end}` : body.days ? `Featured for ${body.days} days${body.auto_renew ? " (auto-renews)" : ""}` : "Featured with no end date") : "Removed from featured");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update featured"); }
    finally { setBusy(false); }
  };

  const onTrigger = async (e) => {
    stop(e);
    if (busy) return;
    if (feat) return post({ featured: false });
    const d = await loadDefaults();
    setCfg(d); setDays(d.days); setRenew(d.auto_renew); setOpen(true);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button onClick={onTrigger} data-testid={`feature-btn-${listing.id}`} disabled={busy}
          aria-label={feat ? "Unfeature listing" : "Feature listing"} title={scheduled ? `Scheduled from ${scheduled.slice(0, 10)} — click to cancel` : feat ? "Remove from category spotlight" : "Feature in category spotlight"}
          data-scheduled={scheduled ? "yes" : "no"}
          className={`absolute ${bottom ? "bottom-3" : "top-3"} right-[3.25rem] z-10 h-9 w-9 rounded-full backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 transition-transform disabled:opacity-60 ${scheduled ? "bg-amber-400" : feat ? "bg-[#F5551E]" : "bg-white/95"}`}>
          {busy ? <Loader2 size={15} className="animate-spin text-slate-500" /> : <Star size={16} className={feat ? "fill-white text-white" : "text-slate-500"} />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-3 bg-white z-[70]" onClick={stop} data-testid={`feature-menu-${listing.id}`}>
        <p className="text-xs font-bold text-[#12234A] uppercase tracking-wider mb-2">Feature for</p>
        <div className="grid grid-cols-2 gap-1.5">
          {(cfg?.options || [3, 7, 14, 30, 0]).filter((d) => d !== 0).concat(0).map((d) => (
            <button key={d} type="button" onClick={(e) => { stop(e); setDays(d); }} data-testid={`feature-days-${d}-${listing.id}`}
              className={`h-8 rounded-lg text-xs font-semibold border transition-colors ${d === 0 ? "col-span-2" : ""} ${days === d ? "bg-[#12234A] text-white border-[#12234A]" : "border-slate-200 text-slate-600 hover:border-[#F5551E]"}`}>
              {d === 0 ? "No end date" : `${d} days`}{cfg && cfg.days === d ? " · default" : ""}
            </button>
          ))}
        </div>
        <button type="button" disabled={days === 0} onClick={(e) => { stop(e); setRenew((r) => !r); }} data-testid={`feature-renew-${listing.id}`}
          className={`mt-2.5 w-full flex items-center gap-2 text-xs font-semibold px-2 py-1.5 rounded-lg transition-colors disabled:opacity-40 ${renew && days ? "bg-emerald-50 text-emerald-700" : "text-slate-500 hover:bg-slate-50"}`}>
          <span className={`h-4 w-4 rounded border flex items-center justify-center ${renew && days ? "bg-emerald-600 border-emerald-600" : "border-slate-300"}`}>{renew && days ? <RefreshCw size={10} className="text-white" /> : null}</span>
          Auto-renew when it ends
        </button>
        <div className="mt-2.5 grid grid-cols-2 gap-1.5" onClick={stop}>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Start
            <input type="date" value={start} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setStart(e.target.value)} className="mt-0.5 w-full h-8 rounded-lg border border-slate-200 px-1.5 text-xs text-[#12234A]" data-testid={`feature-start-${listing.id}`} /></label>
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">End
            <input type="date" value={end} min={start || new Date().toISOString().slice(0, 10)} onChange={(e) => setEnd(e.target.value)} className="mt-0.5 w-full h-8 rounded-lg border border-slate-200 px-1.5 text-xs text-[#12234A]" data-testid={`feature-end-${listing.id}`} /></label>
        </div>
        <p className="text-[10px] text-slate-400 mt-1">Optional. An end date replaces the days above.</p>
        <button type="button" onClick={(e) => { stop(e); post({ featured: true, days: days || null, auto_renew: !!(renew && days && !end), start, end }); }} disabled={busy}
          data-testid={`feature-confirm-${listing.id}`}
          className="mt-2.5 w-full h-9 rounded-lg bg-[#F5551E] hover:bg-[#E04812] text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60">
          <Star size={14} className="fill-white" /> Feature listing
        </button>
      </PopoverContent>
    </Popover>
  );
}
